import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Eye, PauseCircle, RefreshCw } from "lucide-react";
import { formatCoolingRemaining, getCoolingProgress } from "../utils/sessionCoolingHelpers";
import { tickCoolingRemaining, pingSessionHeartbeat } from "../redux/sessionCoolingSlice";
import { selectAppIsAuthenticated } from "../redux/appAuthSlice";
import "../session-cooling.css";

function StatusPill({ icon: Icon, label, tone = "neutral" }) {
  return (
    <span className={`session-cooling-pill session-cooling-pill-${tone}`}>
      <Icon size={14} aria-hidden />
      {label}
    </span>
  );
}

export default function SessionCoolingOverlay() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const { visible, coolingRemainingMs, active, coolingDurationMs } =
    useSelector((s) => s.sessionCooling);

  useEffect(() => {
    if (!visible || !active) return undefined;
    const timer = window.setInterval(() => {
      dispatch(tickCoolingRemaining());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [visible, active, dispatch]);

  useEffect(() => {
    if (!visible || !active || coolingRemainingMs > 15000) return undefined;
    const timer = window.setInterval(() => {
      dispatch(pingSessionHeartbeat());
    }, 10000);
    return () => window.clearInterval(timer);
  }, [visible, active, coolingRemainingMs, dispatch]);

  if (!isAuthenticated || !visible) return null;

  const progress = getCoolingProgress(coolingRemainingMs, coolingDurationMs);
  const progressDeg = `${progress * 360}deg`;

  return (
    <div className="session-cooling-overlay" role="dialog" aria-modal="true" aria-labelledby="session-cooling-title">
      <div className="session-cooling-card">
        <div className="session-cooling-card-glow session-cooling-card-glow-top" aria-hidden />
        <div className="session-cooling-card-glow session-cooling-card-glow-bottom" aria-hidden />

        <h1 id="session-cooling-title" className="session-cooling-title">
          Take a break
        </h1>

        <div className="session-cooling-ring" style={{ "--cooling-progress": progressDeg }}>
          <div className="session-cooling-ring-inner">
            <strong className="session-cooling-timer-value">
              {formatCoolingRemaining(coolingRemainingMs)}
            </strong>
          </div>
        </div>

        <div className="session-cooling-pills">
          <StatusPill icon={Eye} label="Browse" tone="ok" />
          <StatusPill icon={PauseCircle} label="Paused" tone="paused" />
          <StatusPill icon={RefreshCw} label="Auto" tone="neutral" />
        </div>

        <p className="session-cooling-hint">Resumes automatically.</p>
      </div>
    </div>
  );
}
